import { useQuery } from '@apollo/client/react'

import {
  Flex,
  Skeleton,
  Table,
  Button,
  Separator,
  ExternalLinkIcon,
  Text,
} from '@buffer-mono/popcorn'

import {
  useHasOrganizationAction,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { formatCurrency } from './PlanContent'
import { getSettingsUrl } from '../../shared/utils/getSettingsUrl'
import { SettingsRow } from '../../shared/components/SettingsRow'
import { useGetPortalSession } from '../hooks/useGetPortalSession'

import style from './Invoices.module.css'

import type { JSX } from 'react'

const InvoicesQuery = graphql(/* GraphQL */ `
  query Invoices($organizationId: String!, $first: Int, $after: String) {
    invoicesByOrganization(
      organizationId: $organizationId
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          amount
          status
          downloadUrl
          date
          currency
        }
      }
      pageInfo {
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`)

export const Invoices_BillingDetails = graphql(/* GraphQL */ `
  fragment Invoices_BillingDetails on BillingDetails {
    gateway {
      type
    }
    previousGateway {
      type
    }
  }
`)

// from 2025-03-31T17:27:53.000Z to July 17, 2024 style
function formatDate(date: string): string {
  const dateObj = new Date(date)
  return dateObj.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export const Invoices = ({
  billingDetails,
}: {
  // Optional because the MPBilling branch can't fetch `billing(input:)`
  // (backend errors); invoice list still renders, gateway-transition notice
  // just skips.
  billingDetails?: FragmentOf<typeof Invoices_BillingDetails>
}): JSX.Element => {
  const organizationId = useOrganizationId()
  const canEdit = useHasOrganizationAction('manageBilling')
  const { data, loading, error } = useQuery(InvoicesQuery, {
    variables: {
      organizationId: organizationId ?? '',
      first: 3,
    },
    skip: !organizationId || !canEdit,
  })

  const billing = billingDetails
    ? readFragment(Invoices_BillingDetails, billingDetails)
    : null

  const { getCustomerPortalSession, loading: portalSessionLoading } =
    useGetPortalSession()

  const invoices = data?.invoicesByOrganization.edges
    .map((edge) => edge?.node)
    .filter((invoice) => invoice !== undefined)

  if (!invoices || invoices.length === 0 || error) {
    return <></>
  }

  const showStripeToRevenueCatNotice =
    billing?.previousGateway?.type === 'stripe' &&
    billing?.gateway?.type === 'revenuecat'

  return (
    <>
      <Separator />
      <Skeleton show={loading}>
        <SettingsRow id="invoices">
          <SettingsRow.Header>
            <SettingsRow.Title>Invoices &amp; Receipts</SettingsRow.Title>
          </SettingsRow.Header>
          <SettingsRow.Control>
            <Button
              variant="secondary"
              onClick={(): void => {
                getCustomerPortalSession({
                  returnURL: getSettingsUrl(),
                  cta: 'billing-invoices-viewAll-1',
                })
              }}
              disabled={portalSessionLoading}
            >
              View All
              <ExternalLinkIcon />
            </Button>
          </SettingsRow.Control>
        </SettingsRow>
      </Skeleton>
      {showStripeToRevenueCatNotice && (
        <Flex
          direction="column"
          align="center"
          gap="lg"
          fullWidth
          className={style.invoicesInfo}
        >
          <Text size="md" align="left" lineHeight="regular">
            Your current invoices can be accessed through your app store, and
            your previous web-based invoices are available below.
          </Text>
        </Flex>
      )}
      <Skeleton show={loading}>
        <Table fullWidth>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Date</Table.HeaderCell>
              <Table.HeaderCell>Amount</Table.HeaderCell>
              <Table.HeaderCell></Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {invoices?.map((invoice) => (
              <Table.Row key={invoice.id}>
                <Table.Cell>{formatDate(invoice.date)}</Table.Cell>
                <Table.Cell>${formatCurrency(invoice.amount)}</Table.Cell>
                <Table.Cell align="right">
                  <Button
                    size="small"
                    variant="tertiary"
                    as="a"
                    href={invoice.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--color-text-neutral)',
                      fontWeight: 'medium',
                      display: 'inline-flex',
                    }}
                  >
                    View
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Skeleton>
    </>
  )
}

export default Invoices
